import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  message,
  Space,
  Alert,
  Card,
  Table,
  Tag,
  Divider,
  Empty,
} from 'antd';
import {
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  HistoryOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { FinancialService } from '../../service/financial-service/financial.service';
import {
  FinancialRecordRequestDto,
  FinancialRecordType,
  FinancialRecordResponseDto,
} from '../../model/Financial.model';
import useEmployee from '../../hook/useEmployee';
import useRole from '../../hook/useRole';

const { TextArea } = Input;

interface PayCustomerModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  transaction: {
    id: number;
    amount: number;
    balance: number; // Số tiền sau POS
    creditCard?: {
      cardNumber: string;
      cardHolderName?: string;
      customer?: { name?: string };
    };
    transactionDate: string;
    feeForCustomer?: number;
    customerType?: string;
  };
}

const PayCustomerModal: React.FC<PayCustomerModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  transaction,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [relatedRecords, setRelatedRecords] = useState<
    FinancialRecordResponseDto[]
  >([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const { employee } = useEmployee();
  const role = useRole();

  const formatCardNumber = (cardNumber: string) => {
    return cardNumber ? `****${cardNumber.slice(-4)}` : 'N/A';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  // Load related financial records when modal opens
  useEffect(() => {
    if (visible && transaction.id) {
      loadRelatedRecords();
    }
  }, [visible, transaction.id]);

  const loadRelatedRecords = async () => {
    try {
      setLoadingRecords(true);
      const records = await FinancialService.getRecordsByTransactionId(
        transaction.id
      );
      setRelatedRecords(records);
    } catch (error) {
      console.error('Error loading related records:', error);
      message.error('Không thể tải danh sách bản ghi chi liên quan');
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!employee) {
        message.error('Không thể xác định thông tin người dùng');
        return;
      }

      const requestData: FinancialRecordRequestDto = {
        createdByEmployeeId: employee.id,
        recordType: FinancialRecordType.EXPENSE,
        category: 'Khách hàng',
        amount: values.amount || transaction.balance,
        recordDate: values.recordDate
          ? values.recordDate.format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        source: 'Giao dịch rút tiền',
        description: `${transaction.creditCard?.customer?.name || transaction.creditCard?.cardHolderName || 'N/A'} - ***${transaction.creditCard?.cardNumber?.slice(-4) || ''}`,
        referenceNumber: `TXN-${transaction.id}`,
        relatedTransactionId: transaction.id,
      };

      await FinancialService.createRecord(requestData);
      message.success('Đã ghi nhận trả tiền cho khách thành công!');
      form.resetFields();
      // Reload related records after creating new one
      loadRelatedRecords();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating payment record:', error);
      message.error(
        error.response?.data || 'Có lỗi xảy ra khi ghi nhận thanh toán'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setRelatedRecords([]);
    onCancel();
  };

  const handleDeleteRecord = async (recordId: number) => {
    try {
      await FinancialService.deleteRecord(recordId);
      message.success('Đã xóa bản ghi thành công!');
      loadRelatedRecords(); // Reload after delete
    } catch (error) {
      console.error('Error deleting record:', error);
      message.error('Không thể xóa bản ghi này');
    }
  };

  const getRoleColor = (role: string | null) => {
    switch (role) {
      case 'ROLE_ADMIN':
        return 'red';
      case 'ROLE_EMPLOYEE':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getRoleText = (role: string | null) => {
    switch (role) {
      case 'ROLE_ADMIN':
        return 'Admin';
      case 'ROLE_EMPLOYEE':
        return 'Nhân viên';
      default:
        return 'Không xác định';
    }
  };

  console.log(
    'feeForCustomer:',
    transaction.feeForCustomer,
    'customerType:',
    transaction.customerType
  );

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <DollarOutlined className="text-green-600" />
          <span>Trả tiền cho khách</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={680}
      footer={[
        <Button key="cancel" onClick={handleCancel} size="small">
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<DollarOutlined />}
          loading={loading}
          onClick={handleSubmit}
          disabled={!employee}
          size="small"
        >
          Ghi nhận trả tiền
        </Button>,
      ]}
    >
      {/* Compact Transaction Info */}
      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <div>GD #{transaction.id}</div>
          <div className="font-medium">
            {formatCardNumber(transaction.creditCard?.cardNumber || '')}
          </div>
          <div className="text-gray-600">Chủ thẻ:</div>
          <div className="font-medium">
            {transaction.creditCard?.cardHolderName || 'N/A'}
          </div>
          <div className="text-gray-600">Sau phí:</div>
          <div className="font-semibold text-green-600">
            {formatCurrency(
              transaction.feeForCustomer && transaction.customerType === '%'
                ? transaction.amount -
                    (transaction.amount * transaction.feeForCustomer) / 100
                : transaction.amount
            )}
          </div>
        </div>
      </div>

      {/* Compact User Info */}
      {employee && role && (
        <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
          <div className="flex justify-between items-center">
            <span>
              <UserOutlined className="mr-1" />
              {employee.name} ({getRoleText(role)})
            </span>
            <span className="text-orange-600">
              {role === 'ROLE_ADMIN' ? 'AccountBalance' : 'EmployeeFund'}
            </span>
          </div>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          recordDate: dayjs(),
        }}
        size="small"
      >
        <div className="grid grid-cols-2 gap-3">
          <Form.Item
            name="amount"
            label="Số tiền trả"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền' },
              { type: 'number', min: 0.01, message: 'Số tiền phải lớn hơn 0' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Nhập số tiền"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value!.replace(/,/g, '')}
              addonAfter="VND"
              size="small"
            />
          </Form.Item>

          <Form.Item
            name="recordDate"
            label="Ngày trả tiền"
            rules={[{ required: true, message: 'Vui lòng chọn ngày trả tiền' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              showTime
              placeholder="Chọn ngày"
              format="DD/MM/YYYY HH:mm"
              size="small"
            />
          </Form.Item>
        </div>

        <Form.Item name="note" label="Ghi chú" className="mb-3">
          <TextArea
            placeholder="Ghi chú bổ sung (tùy chọn)"
            rows={2}
            showCount
            maxLength={100}
            size="small"
          />
        </Form.Item>
      </Form>

      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
        💡 Tạo bản ghi CHI danh mục "Khách hàng" liên kết GD #{transaction.id}
      </div>

      {/* Related Records Table */}
      <Divider orientation="left" orientationMargin={0}>
        <span className="text-sm flex items-center">
          <HistoryOutlined className="mr-1" />
          Lịch sử bản ghi chi ({relatedRecords.length})
        </span>
      </Divider>

      {loadingRecords ? (
        <div className="text-center py-4 text-xs text-gray-500">
          Đang tải...
        </div>
      ) : relatedRecords.length > 0 ? (
        <Table
          dataSource={relatedRecords}
          rowKey="id"
          size="small"
          pagination={false}
          className="mt-2"
          scroll={{ y: 150 }}
          columns={[
            {
              title: 'Ngày',
              dataIndex: 'recordDate',
              key: 'recordDate',
              width: 80,
              render: (date: string) => dayjs(date).format('DD/MM HH:mm'),
            },
            {
              title: 'Loại',
              dataIndex: 'recordType',
              key: 'recordType',
              width: 50,
              render: (type: string) => (
                <Tag color={type === 'INCOME' ? 'green' : 'red'}>
                  <span className="text-xs">
                    {type === 'INCOME' ? 'Thu' : 'Chi'}
                  </span>
                </Tag>
              ),
            },
            {
              title: 'Số tiền',
              dataIndex: 'amount',
              key: 'amount',
              width: 100,
              render: (amount: number) => (
                <span className="text-xs font-medium">
                  {formatCurrency(amount)}
                </span>
              ),
            },
            {
              title: 'Danh mục',
              dataIndex: 'category',
              key: 'category',
              width: 80,
              render: (category: string) => (
                <span className="text-xs">{category || 'N/A'}</span>
              ),
            },
            {
              title: 'Người tạo',
              dataIndex: 'createdBy',
              key: 'createdBy',
              width: 80,
              render: (createdBy: string) => (
                <span className="text-xs">{createdBy}</span>
              ),
            },
            {
              title: '',
              key: 'action',
              width: 40,
              render: (record: FinancialRecordResponseDto) => (
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm('Xóa bản ghi này?')) {
                      handleDeleteRecord(record.id);
                    }
                  }}
                  className="text-red-500 hover:bg-red-50"
                />
              ),
            },
          ]}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-xs text-gray-500">
              Chưa có bản ghi chi nào cho giao dịch này
            </span>
          }
          className="py-4"
        />
      )}
    </Modal>
  );
};

export default PayCustomerModal;
